/**
 * Sprint 1b-3 — the issuance state, on a list row.
 *
 * The existing status badge answers "what status column does this row have".
 * That is not the question the front desk has after Sprint 1, which is "has the
 * report actually gone out". A confirmed-but-not-issued assessment carries
 * status 'in_progress' and looks exactly like an untouched draft.
 *
 * Rendered ALONGSIDE the existing badge and only when it adds something: for a
 * plain draft the status badge already says Draft, so this stays out of the way.
 */
import { useTranslation } from 'react-i18next';
import { lifecycleLabel, lifecycleState, type LifecycleInput } from '@/lib/assessmentLifecycleState';

const TONE_CLASS: Record<string, string> = {
  neutral: 'bg-gray-100 text-gray-700',
  attention: 'bg-amber-100 text-amber-800',
  done: 'bg-green-100 text-green-800',
};

export function LifecycleBadge({ assessment }: { assessment: LifecycleInput }): JSX.Element | null {
  const { t } = useTranslation();
  const state = lifecycleState(assessment);
  if (state === 'draft') return null;   // the status badge already says this

  const label = lifecycleLabel(assessment);
  return (
    <span
      data-testid={`lifecycle-badge-${state}`}
      className={`text-xs px-2 py-0.5 rounded-full font-medium ${TONE_CLASS[label.tone]}`}
    >
      {t(label.key, { defaultValue: label.fallback, ...(label.params ?? {}) })}
    </span>
  );
}
